import { useState, useEffect } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { useLocation, useNavigate } from 'react-router-dom';

import { View, Pressable } from 'react-native-web';
import CustomText from '../components/atoms/CustomText';
import Footer from '../components/layout/Footer';
import { colors, spacing, borderRadius, badgeStyle } from '../utils/styling';
import GeneralDialog from '../components/common/GeneralDialog';
import { downloadPDF, generatePDFBlob } from '../lib/pdf-utils';
import { sendPdfEmail, trackPDFDownload } from '../lib/stripe';
import { uploadPDFToSupabaseSimple } from '../lib/supabase-pdf';
import { CATEGORY_SECTIONS, SPECIFIC_CATEGORY_SECTIONS } from '../utils/category_mappings';
import { resolveWorkbookExercises } from '../utils/quizinfo/personalityContent';
import { loadDataWithVersionCheck } from "../utils/versionManager";
import { ResultsData } from '../utils/categoryMigration';
import { clearMoneyPersonalityAssessmentState } from '../utils/quizRetake';

import HeroSection from '../components/home/HeroSection';
import WhySection from '../components/home/WhySection';
import { useScrollReveal } from '../hooks/useScrollReveal';
import '../fonts/fonts.css';
import '../App.css';

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();
  useScrollReveal();

  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'download' | 'info-collection' | 'main-results'>('download');
  const [isEmailing, setIsEmailing] = useState<boolean>(false);
  const [isUser, setIsUser] = useState(false);
  const [isManualDownloading, setIsManualDownloading] = useState(false);
  const [transactionData, setTransactionData] = useState<{
    status: string;
    transaction_id: string | null;
    error?: string;
  } | null>(null);

  let resultsData = location.state as ResultsData | undefined;

  if (!resultsData) {
    // resultsData not in state, check local storage
    const stored = localStorage.getItem("mpay_resultsData");
    if (stored) {
      try {
        let parsedData = JSON.parse(stored);
        
        // Apply migration if needed
        parsedData = loadDataWithVersionCheck(parsedData, 'localStorage');
        
        resultsData = parsedData;
      } catch (e) {
        console.error("Error parsing/migrating localStorage data:", e);
      }
    } else {
      console.log('No localStorage data found');
    }
  }

  const defaultResultsData: ResultsData = {
    answers: [],
    categoryScores: {},
    dominantCategory: "",
    sfAccount: null,
    quizId: undefined,
    leadId: undefined,
    email: undefined,
  };

  // Use resultsData if available, otherwise use defaults
  const safeResultsData = resultsData || defaultResultsData;

  const {
    email,
    dominantCategory,
    answers,
    categoryScores,
    specificAnswers,
    specificCategoryScores,
    specificDominantCategory,
    sfAccount,
    quizId,
  } = safeResultsData;


  const handleQuizNavigation = () => {
    // Check if they already took quiz 
    const stored = localStorage.getItem('mpay_resultsData');
    if (stored) {
      // If taken quiz, check if they went through info collection to download PDF
      // TODO this is for paying logic
      // const hasPaid = (transactionData?.status === 'found' && transactionData.transaction_id) || localStorage.getItem('payment_success') !== null || localStorage.getItem('mpay_coupon_redeemed') !== null;
      // const personalInfo = localStorage.getItem('mpay_personalInfo');
      // const hasCompletedInfoCollection = personalInfo !== null;
      // const showDownload = hasPaid && hasCompletedInfoCollection;

      // TODO this is for free month
      const hasPaid = true;
      const personalInfo = localStorage.getItem('mpay_personalInfo');
      const hasCompletedInfoCollection = personalInfo !== null;
      const showDownload = hasPaid && hasCompletedInfoCollection;
      // If they already went through the info collection process, then give them option to 
      // download their PDF + send it to them
      if (showDownload) {
        setDialogType('download');
        setShowDialog(true);
      } else if (hasPaid) {  // they have taken quiz (and paid) but not info collection, so suggest them to complete it + navigate
        setDialogType('info-collection');
        setShowDialog(true);
      } else { // they have taken the quiz only, without paying, so go back to main results
        setDialogType('main-results');
        setShowDialog(true);
      }
    } else {  // haven't taken quiz, so navigate there
      navigate('/quiz', { state: { from: location.pathname }} );
    }
  };

  const handleEmailPDF = async () => {
    setIsEmailing(true);
    
    try {
      // Get results data from localStorage
      const resultsData = JSON.parse(localStorage.getItem('mpay_resultsData') || '{}');
      const personalInfo = JSON.parse(localStorage.getItem('mpay_personalInfo') || '{}');
      
      if (!resultsData.email || !resultsData.quizId) {
        console.error('Missing email or quizId in results data');
        return;
      }

      // Get transaction ID
      let transactionId = "Client";
      if (transactionData?.status === 'found' && transactionData.transaction_id) {
        transactionId = transactionData.transaction_id;
      }

      // Generate PDF
      console.log('📄 Generating PDF...');
      const pdfBlob = await generatePDFBlob({
        answers: resultsData.answers || [],
        categoryScores: resultsData.categoryScores || {},
        dominantCategory: resultsData.dominantCategory || "",
        personalInfo: personalInfo
      });

      // Upload to Supabase
      const emailHash = resultsData.email.replace(/[^a-zA-Z0-9]/g, '');
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `personality-profile-${emailHash}-${dateStr}`;
      
      const uploadResult = await uploadPDFToSupabaseSimple(pdfBlob, filename);
      
      // Track PDF download
      if (resultsData.quizId && resultsData.email) {
        try {
          await trackPDFDownload(resultsData.quizId, resultsData.email, filename, uploadResult.url);
        } catch (trackError) {
          console.warn('⚠️ PDF tracking failed (non-critical):', trackError);
        }
      }

      // Send email
      console.log('📧 Sending email...');
      await sendPdfEmail(
        resultsData.email,
        resultsData.quizId,
        resultsData,
        transactionId,
        CATEGORY_SECTIONS,
        SPECIFIC_CATEGORY_SECTIONS,
        resultsData.dominantCategory,
        resultsData.specificDominantCategory,
        uploadResult.url
      );

      console.log('✅ PDF emailed successfully');
    } catch (error) {
      console.error('❌ Failed to email PDF:', error);
    } finally {
      setIsEmailing(false);
    }
  }

  const handleDownloadPDF = async () => {
    setIsManualDownloading(true);
    
    try {
      // Get results data from localStorage  
      const resultsData = JSON.parse(localStorage.getItem('mpay_resultsData') || '{}');
      const personalInfo = JSON.parse(localStorage.getItem('mpay_personalInfo') || '{}');
      
      const filename = `financial-personality-profile-${new Date().toISOString().split('T')[0]}`;
      
      // Download PDF directly
      await downloadPDF({
        answers: resultsData.answers || [],
        categoryScores: resultsData.categoryScores || {},
        dominantCategory: resultsData.dominantCategory || "",
        personalInfo: personalInfo,
        exercises: resolveWorkbookExercises(specificDominantCategory)
      }, filename);
      
      console.log('✅ PDF downloaded successfully');
    } catch (error) {
      console.error('❌ Failed to download PDF:', error);
    } finally {
      setIsManualDownloading(false);
    }
  }

  const handleResultsNavigation = () => {
    // Navigate to info collection
    const resultsData = JSON.parse(localStorage.getItem('mpay_resultsData') || '{}');
    navigate('/quiz/results-ready', { state: resultsData });
  }

  const handlePortalNavigation = () => {
    const resultsData = JSON.parse(localStorage.getItem('mpay_resultsData') || '{}');
    navigate('/login', { state: resultsData })
  }

  const handleRetakeQuiz = () => {
    clearMoneyPersonalityAssessmentState();
    setShowDialog(false);
    navigate('/quiz', { state: { from: location.pathname, retake: true } });
  };

  const renderRetakeButton = () => (
    <Pressable
      onPress={handleRetakeQuiz}
      style={({ hovered }: { hovered: boolean }) => ({
        backgroundColor: hovered ? '#f5f5f5' : 'white',
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.lg,
        border: '1px solid black',
        height: isMobile ? 40 : 50,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      })}
    >
      <CustomText style={{
        color: 'black',
        fontSize: 16,
        fontWeight: '600',
        fontFamily: 'Nunito',
      }}>
        Retake quiz
      </CustomText>
    </Pressable>
  );



  useEffect(() => {
    // @ts-ignore
    if (window.transactionData) {
      // @ts-ignore
      setTransactionData(window.transactionData);
    }
    
    // Listen for changes to global transaction data
    const checkTransactionData = () => {
      // @ts-ignore
      if (window.transactionData && window.transactionData !== transactionData) {
        // @ts-ignore
        setTransactionData(window.transactionData);
      }
    };
    
    const interval = setInterval(checkTransactionData, 1000);
    return () => clearInterval(interval);
  }, [transactionData]);

  return (
    <View style={{ flex: 1, backgroundColor: colors.white, minHeight: '100vh', position: 'relative', alignItems: 'center', width: '100%' }}>
      {/* Hero Section — redesigned 2026-10-05 per McKayla's design Loom */}
      <HeroSection isMobile={isMobile} onTakeQuiz={handleQuizNavigation} />

      {/* Personality Types Section */}
      <View style={{ 
        width: '100%',
        padding: isMobile ? undefined : spacing.xl,
        flexDirection: 'row',
        flexWrap: isMobile ?  'nowrap' : 'wrap',
        overflowX: isMobile ? 'scroll' : 'undefined',
        justifyContent: 'center',
        alignItems: isMobile ? 'center' : 'flex-start',
        gap: isMobile ? spacing.lg : spacing.xl,
        marginBottom: isMobile ? spacing.md * 2 : spacing.lg * 2,
        backgroundColor: '#91e1b8',
        position: 'relative',
      }}>
        <View style={{
          width: '100%',
          gap: '3rem',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          maxWidth: '95%',
          marginHorizontal: 'auto',
          overflow: 'hidden',
        }}>
          <div style={{
            flexDirection: 'row',
            gap: '1rem',
            width: '100%',
            display: 'flex',
            paddingTop: isMobile ? spacing.md : spacing.lg,
            paddingBottom: isMobile ? spacing.md : spacing.lg,
          }}
            className="feature-carousel"
          >
            <div style={{
              padding: spacing.xl,
              border: `1px solid ${colors.gray[200]}`,
              borderRadius: borderRadius.lg,
              minWidth: isMobile ? 250 : 300,
              maxWidth: isMobile ? 350 : 450,
              width: '100%',
              backgroundColor: colors.white,
            }}
              className="carousel-set"
            >
              <img src={require('../components/img/Plant.png')} alt="Plant icon" style={{ width: 40, height: 40, marginBottom: spacing.md }} />
              <CustomText style={{ textAlign: 'left', fontSize: 16, color: colors.gray[700], lineHeight: '24px' }}>
                Discover opportunities to <CustomText style={{ color: '#076652', fontWeight: '600' }}>grow</CustomText>. Learn about your strengths and weaknesses.
              </CustomText>
            </div>

            <div style={{
              padding: spacing.xl,
              border: `1px solid ${colors.gray[200]}`,
              borderRadius: borderRadius.lg,
              minWidth: isMobile ? 250 : 300,
              maxWidth: isMobile ? 350 : 450,
              width: '100%',
              backgroundColor: colors.white,
            }}
              className="carousel-set"
            >
              <img src={require('../components/img/Wired.png')} alt="Wired icon" style={{ width: 40, height: 40, marginBottom: spacing.md }} />
              <CustomText style={{ textAlign: 'left', fontSize: 16, color: colors.gray[700], lineHeight: '24px' }}>
                Understand how your relationship with money is <CustomText style={{ color: '#00A4EF', fontWeight: '600' }}>wired</CustomText>.
              </CustomText>
            </div>

            <div style={{
              padding: spacing.xl,
              border: `1px solid ${colors.gray[200]}`,
              borderRadius: borderRadius.lg,
              minWidth: isMobile ? 250 : 300,
              maxWidth: isMobile ? 350 : 450,
              width: '100%',
              backgroundColor: colors.white,
            }}
              className="carousel-set"
            >
              <img src={require('../components/img/Bird.png')} alt="Bird icon" style={{ width: 40, height: 40, marginBottom: spacing.md }} />
              <CustomText style={{ textAlign: 'left', fontSize: 16, color: colors.gray[700], lineHeight: '24px' }}>
                Learn about what <CustomText style={{ color: '#FF00FF', fontWeight: '600' }}>freedom</CustomText> means to you, and how you enjoy it.
              </CustomText>
            </div>

            <div style={{
              padding: spacing.xl,
              border: `1px solid ${colors.gray[200]}`,
              borderRadius: borderRadius.lg,
              minWidth: isMobile ? 250 : 300,
              maxWidth: isMobile ? 350 : 450,
              width: '100%',
              backgroundColor: colors.white,
            }}
              className="carousel-set"
            >
              <img src={require('../components/img/Mountain.png')} alt="Mountain icon" style={{ width: 40, height: 40, marginBottom: spacing.md }} />
              <CustomText style={{ textAlign: 'left', fontSize: 16, color: colors.gray[700], lineHeight: '24px' }}>
                Give context and meaning to your grand plans and <CustomText style={{ color: '#00B3C2', fontWeight: '600' }}>strategies</CustomText>.
              </CustomText>
            </div>

            <div style={{
              padding: spacing.xl,
              border: `1px solid ${colors.gray[200]}`,
              borderRadius: borderRadius.lg,
              minWidth: isMobile ? 250 : 300,
              maxWidth: isMobile ? 350 : 450,
              width: '100%',
              marginRight: '0.15rem',
              backgroundColor: colors.white,
            }}
              className="carousel-set"
            >
              <img src={require('../components/img/Giving.png')} alt="Giving icon" style={{ width: 40, height: 40, marginBottom: spacing.md }} />
              <CustomText style={{ textAlign: 'left', fontSize: 16, color: colors.gray[700], lineHeight: '24px' }}>
                Learn how you like to <CustomText style={{ color: '#9933FF', fontWeight: '600' }}>give back</CustomText> and help others.
              </CustomText>
            </div>

            {/* Repeated content for smooth carousel */}
            <div style={{
              padding: spacing.xl,
              border: `1px solid ${colors.gray[200]}`,
              borderRadius: borderRadius.lg,
              minWidth: isMobile ? 250 : 300,
              maxWidth: isMobile ? 350 : 450,
              width: '100%',
              backgroundColor: colors.white,
            }}
              className="carousel-set"
            >
              <img src={require('../components/img/Plant.png')} alt="Plant icon" style={{ width: 40, height: 40, marginBottom: spacing.md }} />
              <CustomText style={{ textAlign: 'left', fontSize: 16, color: colors.gray[700], lineHeight: '24px' }}>
                Discover opportunities to <CustomText style={{ color: '#076652', fontWeight: '600' }}>grow</CustomText>. Learn about your strengths and weaknesses.
              </CustomText>
            </div>

            <div style={{
              padding: spacing.xl,
              border: `1px solid ${colors.gray[200]}`,
              borderRadius: borderRadius.lg,
              minWidth: isMobile ? 250 : 300,
              maxWidth: isMobile ? 350 : 450,
              width: '100%',
              backgroundColor: colors.white,
            }}
              className="carousel-set"
            >
              <img src={require('../components/img/Wired.png')} alt="Wired icon" style={{ width: 40, height: 40, marginBottom: spacing.md }} />
              <CustomText style={{ textAlign: 'left', fontSize: 16, color: colors.gray[700], lineHeight: '24px' }}>
                Understand how your relationship with money is <CustomText style={{ color: '#00A4EF', fontWeight: '600' }}>wired</CustomText>.
              </CustomText>
            </div>

            <div style={{
              padding: spacing.xl,
              border: `1px solid ${colors.gray[200]}`,
              borderRadius: borderRadius.lg,
              minWidth: isMobile ? 250 : 300,
              maxWidth: isMobile ? 350 : 450,
              width: '100%',
              backgroundColor: colors.white,
            }}
              className="carousel-set"
            >
              <img src={require('../components/img/Bird.png')} alt="Bird icon" style={{ width: 40, height: 40, marginBottom: spacing.md }} />
              <CustomText style={{ textAlign: 'left', fontSize: 16, color: colors.gray[700], lineHeight: '24px' }}>
                Learn about what <CustomText style={{ color: '#FF00FF', fontWeight: '600' }}>freedom</CustomText> means to you, and how you enjoy it.
              </CustomText>
            </div>

            <div style={{
              padding: spacing.xl,
              border: `1px solid ${colors.gray[200]}`,
              borderRadius: borderRadius.lg,
              minWidth: isMobile ? 250 : 300,
              maxWidth: isMobile ? 350 : 450,
              width: '100%',
              backgroundColor: colors.white,
            }}
              className="carousel-set"
            >
              <img src={require('../components/img/Mountain.png')} alt="Mountain icon" style={{ width: 40, height: 40, marginBottom: spacing.md }} />
              <CustomText style={{ textAlign: 'left', fontSize: 16, color: colors.gray[700], lineHeight: '24px' }}>
                Give context and meaning to your grand plans and <CustomText style={{ color: '#00B3C2', fontWeight: '600' }}>strategies</CustomText>.
              </CustomText>
            </div>

            <div style={{
              padding: spacing.xl,
              border: `1px solid ${colors.gray[200]}`,
              borderRadius: borderRadius.lg,
              minWidth: isMobile ? 250 : 300,
              maxWidth: isMobile ? 350 : 450,
              width: '100%',
              backgroundColor: colors.white,
            }}
              className="carousel-set"
            >
              <img src={require('../components/img/Giving.png')} alt="Giving icon" style={{ width: 40, height: 40, marginBottom: spacing.md }} />
              <CustomText style={{ textAlign: 'left', fontSize: 16, color: colors.gray[700], lineHeight: '24px' }}>
                Learn how you like to <CustomText style={{ color: '#9933FF', fontWeight: '600' }}>give back</CustomText> and help others.
              </CustomText>
            </div>
          </div>
        </View>
      </View>

      <View style={{
        width: '100%',
        maxWidth: 1300,
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'center',
        alignItems: isMobile ? 'center' : undefined,
        gap: isMobile ? 32 : 70,
        padding: isMobile ? 24 : 64,
        background: '#fff',
        marginBottom: isMobile ? 12 : 42,
        marginTop: isMobile ? 24 : 42,
        marginHorizontal: 'auto',
      }}>
        {/* Left: Purple rectangle with iPhone and badges absolutely positioned */}
        <View style={{
          position: 'relative',
          minWidth: isMobile ? 325 : 475,
          width: '100%',
          maxWidth: isMobile ? 450 : 750,
          height: isMobile ? 375 : 500,
          minHeight: isMobile ? 340 : 500,
          background: '#e5ddf9',
          borderRadius: 18,
          border: '4px solid #7f53fe',
          marginBottom: isMobile ? 60 : 50,
          boxSizing: 'border-box',
          overflow: 'visible',
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {/* iPhone image */}
          <img
            src={require('../components/img/iphone.png')}
            alt="iPhone"
            style={{
              position: 'absolute',
              left: isMobile ? '10%' : 60,
              top: isMobile ? '-10%' : -60,
              width: isMobile ? 200 : 300,
              height: 'auto',
              backgroundColor: 'none',
              borderRadius: 24,
              transform: 'rotate(-4deg)',
              zIndex: 2,
            }}
          />
          {/* Badges */}
          <View
            style={{
              position: 'absolute',
              right: isMobile ? 15 : 30,
              top: isMobile ? 60 : 110,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: isMobile ? 8 : 18,
              zIndex: 3,
            }}
          >
            <View style={badgeStyle(isMobile)}>
              <img src={require('../components/img/check.png')} alt="Check Mark" width="40" height="40"/>
              <CustomText style={{
                fontFamily: 'Nunito',
              }}>
                Certified Behavioral Finance Method™
              </CustomText>
            </View>
            <View style={badgeStyle(isMobile)}>
              <img src={require('../components/img/check.png')} alt="Check Mark" width="40" height="40"/>
              <CustomText style={{
                fontFamily: 'Nunito',
              }}>
                Backed by Dual-Discipline Design™
              </CustomText>
            </View>
            <View style={badgeStyle(isMobile)}>
              <img src={require('../components/img/check.png')} alt="Check Mark" width="40" height="40"/>
              <CustomText style={{
                fontFamily: 'Nunito',
              }}>
                Certified Advisor-Tested
              </CustomText>
            </View>
          </View>
        </View>
        {/* Right: Use your results */}
        <View style={{
          background: '#fdf5cd',
          borderRadius: 18,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          padding: isMobile ? 20 : 36,
          paddingBottom: isMobile ? 18 : 26,
          minWidth: isMobile ? 325 : 300,
          width: '100%',
          maxWidth: isMobile ? 450 : 750,
          minHeight: isMobile ? 340 : 400,
          maxHeight: isMobile ? 300 : 500,
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'center',
          flex: 1,
          border: '4px solid #59585b',
        }}>
          <CustomText style={{ 
            textAlign: 'center',
            fontSize: isMobile ? 18 : 24, 
            color: 'black', 
            marginBottom: 12, 
            fontWeight: 400, 
            fontFamily: "Nunito"
          }}>
            Your Money Personality is your <b><i>Financial Blueprint</i></b>. Use it to:
          </CustomText>
          <ul style={{ 
            marginBlockStart: 0, 
            paddingInlineStart: 20,
            color: 'black', 
            fontSize: isMobile ? 14 : 18, 
            marginBottom: 18, 
            marginLeft: 0, 
            fontFamily: "Nunito"
          }}>
            <li>Understand your habits and motivations</li>
            <li>Set goals that align with how you think and feel about money</li>
            <li>Build a plan that actually fits your life</li>
            <li>Train AI to deliver smarter, more personalized financial guidance</li>
          </ul>
          <CustomText style={{ 
            textAlign: 'center', 
            fontWeight: 400, 
            fontSize: isMobile ? 22 : 28, 
            fontFamily: "Nunito",
            alignSelf: 'flex-end',
          }}>
            Start with <span style={{ color: "black", fontWeight: 1000, fontFamily: "Nunito", }}>insight</span>.<br/>
            <span style={{ color: "#64824e", fontWeight: 1000, fontFamily: "Nunito", }}>Grow</span> with intention.
          </CustomText>
        </View>
      </View>

      {/* The "why" section from McKayla's Canva (design Loom 2026-10-05) */}
      <WhySection isMobile={isMobile} />

      {/* Footer */}
      <Footer />

      {showDialog && dialogType === 'download' && (
        <GeneralDialog
          title="Wait... You look familiar!"
          description="It appears you have already taken the Money Personality Assessment. You can download your personalized PDF report or have it emailed to you. You can also login to the portal."
          yesText="Close"
          noText=""
          onConfirm={() => setShowDialog(false)}
          onClose={() => setShowDialog(false)}
        >
          <View style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.md,
            marginTop: spacing.md,
          }}>
            <Pressable
              onPress={handleDownloadPDF}
              disabled={isManualDownloading}
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: isManualDownloading ? colors.gray[400] : (hovered ? '#515A64' : 'black'),
                paddingVertical: spacing.sm,
                paddingHorizontal: spacing.lg,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: isMobile ? 40 : 50,
                cursor: isManualDownloading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isManualDownloading ? 0.7 : 1,
              })}
            >
              <CustomText style={{
                color: colors.white,
                fontSize: 16,
                fontWeight: '600',
                fontFamily: 'Nunito',
              }}>
                {isManualDownloading ? 'Generating PDF...' : 'Download PDF to my device'}
              </CustomText>
            </Pressable>
            
            <Pressable
              onPress={handleEmailPDF}
              disabled={isEmailing}
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: isEmailing ? colors.gray[400] : (hovered ? '#f5f5f5' : 'white'),
                paddingVertical: spacing.sm,
                paddingHorizontal: spacing.lg,
                border: '1px solid black',
                height: isMobile ? 40 : 50,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isEmailing ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isEmailing ? 0.7 : 1,
              })}
            >
              <CustomText style={{
                color: 'black',
                fontSize: 16,
                fontWeight: '600',
                fontFamily: 'Nunito',
              }}>
                {isEmailing ? 'Emailing PDF...' : 'Email me my PDF'}
              </CustomText>
            </Pressable>
            <Pressable
              onPress={handlePortalNavigation}
              disabled={isEmailing}
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: isEmailing ? colors.gray[400] : (hovered ? '#f5f5f5' : 'white'),
                paddingVertical: spacing.sm,
                paddingHorizontal: spacing.lg,
                border: '1px solid black',
                height: isMobile ? 40 : 50,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isEmailing ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isEmailing ? 0.7 : 1,
              })}
            >
              <CustomText style={{
                color: 'black',
                fontSize: 16,
                fontWeight: '600',
                fontFamily: 'Nunito',
              }}>
                Portal Login
              </CustomText>
            </Pressable>
            {renderRetakeButton()}
          </View>
        </GeneralDialog>
      )}

      {showDialog && dialogType === 'info-collection' && (
        <GeneralDialog
          title="Final Steps"
          description="You have already taken the Money Personality assessment. To unlock your personalized PDF report, please complete a quick information collection form in your portal. This will only take about 1 minute."
          yesText="Take me there"
          noText="Cancel"
          onConfirm={handlePortalNavigation}
          onClose={() => setShowDialog(false)}
        >
          <View style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: spacing.md,
            marginBottom: spacing.sm,
          }}>
            <CustomText style={{
              fontSize: 14,
              color: colors.gray[600],
              fontFamily: 'Nunito',
              textAlign: 'center',
            }}>
              Your results are ready and waiting for you!
            </CustomText>
            <View style={{
              width: '100%',
              marginTop: spacing.md,
            }}>
              {renderRetakeButton()}
            </View>
          </View>
        </GeneralDialog>
      )}

      {showDialog && dialogType === 'main-results' && (
        <GeneralDialog
          title="Gain Full Access"
          description="It appears you have taken the Money Personality Quiz already. To view your initial results, please click the button below."
          yesText="Take me to my results"
          noText="Cancel"
          onConfirm={handleResultsNavigation}
          onClose={() => setShowDialog(false)}
        >
          <View style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: spacing.md,
            marginBottom: spacing.sm,
          }}>
            <CustomText style={{
              fontSize: 14,
              color: colors.gray[600],
              fontFamily: 'Nunito',
              textAlign: 'center',
            }}>
              Your results are ready and waiting for you!
            </CustomText>
            <View style={{
              width: '100%',
              marginTop: spacing.md,
            }}>
              {renderRetakeButton()}
            </View>
          </View>
        </GeneralDialog>
      )}
    </View>
  );
};

export default Home;
